import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ProfileSchema } from "@/schema";
import useProfile from "@/hooks/use-profile";
import ProfileFieldView from "./profile-field-view";
import { useDispatch } from "react-redux";
import { getMyCompany } from "@/store/authSlice";
import { Man, Woman, Edit, Save, Cancel } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const EditProfileFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const userId = authUser.id;
  const { updateUser, getUserProfile, isSaveLoading, error } = useProfile();
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();

  const form = useForm<z.infer<typeof ProfileSchema>>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      id: null,
      nickName: "",
      email: "",
      address: "",
      phoneNumber: "",
      role: "Client",
      gender: null,
      accountType: null,
      region: "",
      city: "",
      identityType: "Citizen",
      identityNo: "",
      residenceNo: "",
    },
  });

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  useEffect(() => {
    const selectedRegion = form.watch("region");
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [form.watch("region")]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (userId) {
        try {
          const userData = await getUserProfile();
          if (userData) {
            dispatch(getMyCompany(userData.data.company));
            let dataUser = {
              ...userData.data.user,
              region: isEmpty(userData.data.user.region)
                ? myCompany?.region
                : userData.data.user.region,
              city: isEmpty(userData.data.user?.city)
                ? myCompany?.city
                : userData.data.user?.city,
            };
            form.reset(dataUser);
            form.setValue("identityType", "Citizen");
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };

    fetchUserProfile();
  }, []);

  const onSubmit = async (values: z.infer<typeof ProfileSchema>) => {
    try {
      const submissionData = {
        ...values,
        identityNo:
          values.identityType === "Citizen" ? values.identityNo : null,
        residenceNo:
          values.identityType === "Resident" ? values.residenceNo : null,
      };
      await updateUser(submissionData);
      setIsEditMode(false);
    } catch (err) {
      console.error("Error during profile submission:", err);
    }
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit mode - reset form to original values
      form.reset();
    }
    setIsEditMode(!isEditMode);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
          {/* Header with Edit Mode Indicator */}
          <Card
            elevation={2}
            sx={{
              boxShadow: "none",
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: isEditMode
                ? alpha(theme.palette.warning.main, 0.05)
                : "background.paper",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {t("profile.title")}
                  </Typography>
                  {isEditMode && (
                    <Chip
                      label={t("actions.editing") || "Editing"}
                      color="warning"
                      size="small"
                      icon={<Edit />}
                    />
                  )}
                </Box>
                <Button
                  variant={isEditMode ? "outlined" : "contained"}
                  startIcon={isEditMode ? <Cancel /> : <Edit />}
                  onClick={handleEditToggle}
                  className={!isEditMode ? "!bg-zinc-900" : ""}
                  color={isEditMode ? "error" : "primary"}
                  sx={{ borderRadius: 2 }}
                >
                  {isEditMode ? t("actions.cancel") : t("actions.edit")}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Personal Information Section */}
          <Card
            elevation={2}
            sx={{
              boxShadow: "none",
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("profile.personalInfo") || "Personal Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="nickName"
                    label={t("profile.fields.nickName")}
                    placeholder={t("profile.placeholders.enterNickName")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="email"
                    label={t("profile.fields.email")}
                    placeholder={t("profile.placeholders.enterEmail")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="phoneNumber"
                    label={t("profile.fields.phoneNumber")}
                    placeholder={t("profile.placeholders.enterPhoneNumber")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="address"
                    label={t("company.fields.address")}
                    placeholder={t("profile.placeholders.enterAddress")}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Location Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.location") || "Location Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="region"
                    label={t("company.fields.region")}
                    placeholder={t("company.placeholders.selectRegion")}
                    inputType="combobox"
                    options={regions}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="city"
                    label={t("company.fields.city")}
                    placeholder={t("company.placeholders.selectCity")}
                    inputType="combobox"
                    options={cities}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Account Information Section */}
          <Card
            elevation={2}
            sx={{
              boxShadow: "none",
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("profile.accountInfo") || "Account Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="gender"
                    label={t("profile.fields.gender")}
                    placeholder={t("profile.placeholders.selectGender")}
                    inputType="radio"
                    options={[
                      {
                        value: "Male",
                        label: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Man sx={{ fontSize: 20 }} />
                            {t("profile.options.gender.male")}
                          </Box>
                        ),
                      },
                      {
                        value: "Female",
                        label: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Woman sx={{ fontSize: 20 }} />
                            {t("profile.options.gender.female")}
                          </Box>
                        ),
                      },
                    ]}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="accountType"
                    label={t("profile.fields.accountType")}
                    placeholder={t("profile.placeholders.selectAccountType")}
                    inputType="radio"
                    options={[
                      {
                        value: "Individual",
                        label: t("profile.options.accountType.individual"),
                      },
                      {
                        value: "Establishment",
                        label: t("profile.options.accountType.establishment"),
                      },
                    ]}
                    formControl={form.control}
                    disabled={!isEditMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Fade in={!!error}>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${theme.palette.error.main}`,
                  borderRadius: 2,
                }}
              >
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Paper>
            </Fade>
          )}

          {/* Save Button */}
          {isEditMode && (
            <Fade in={isEditMode}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleEditToggle}
                      disabled={isSaveLoading}
                      sx={{ borderRadius: 2 }}
                    >
                      {t("actions.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={isSaveLoading}
                      sx={{ borderRadius: 2 }}
                    >
                      {isSaveLoading ? t("actions.saving") : t("actions.save")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          )}
        </Box>
      </form>
    </Form>
  );
};

export default EditProfileFormView;
